import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import avatarCardImg from '@/assets/images/avatar-card.webp';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture } from '@/lib/posthog';
import { toTitleCase } from '@/utils/toTitleCase';
import type { ChangeEvent, FormEvent } from 'react';

interface AddPatientFlowModalProps {
    onManual: () => void;
    onSendSurveyLink: (patientName: string) => void;
}

export default function AddPatientFlowModal({
    onManual,
    onSendSurveyLink,
}: AddPatientFlowModalProps) {
    const [step, setStep] = useState<'choose' | 'name'>('choose');
    const [patientName, setPatientName] = useState('');
    const patientLinkAccess = useFeatureEntitlement('patientLinks.send');
    const activePatientAccess = useFeatureEntitlement('patients.active');
    const trimmedName = patientName.trim();
    const canSendLink = trimmedName.length > 0;

    // Opening this modal is the moment a user decides to add their first
    // patient. Pairing it with the fork below separates "never tried" from
    // "tried and backed out" — the two have completely different fixes.
    useEffect(() => {
        capture('add_patient_modal_opened', { source: 'patients_page' });
    }, []);

    const goToNameStep = () => {
        if (patientLinkAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(patientLinkAccess.decision));
            return;
        }

        capture('add_patient_method_chosen', {
            method: 'intake_link',
            source: 'patients_page',
        });
        setStep('name');
    };

    const handleSubmitLink = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!canSendLink) return;

        onSendSurveyLink(trimmedName);
    };

    const handleManual = () => {
        if (activePatientAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(activePatientAccess.decision));
            return;
        }

        capture('add_patient_method_chosen', { method: 'manual', source: 'patients_page' });
        onManual();
    };

    return (
        <AppDialogLayout
            title={
                step === 'name'
                    ? 'Enviar link de formulário'
                    : 'Como você quer adicionar o paciente?'
            }
            description={
                step === 'name' ? 'Informe o nome do paciente para gerar o link.' : undefined
            }
            backAction={
                step === 'name' ? { label: 'Voltar', onClick: () => setStep('choose') } : undefined
            }
        >
            <div className="space-y-4 sm:space-y-6">
                {/* Rendered on both steps: the dialog width is fixed for the modal's
                    lifetime, so keeping the art is what stops the box from collapsing
                    and re-centering when the step swaps. */}
                <img
                    src={avatarCardImg}
                    alt=""
                    // Intrinsic size, so the browser reserves the box instead of
                    // reflowing the cards when the art lands.
                    width={917}
                    height={833}
                    decoding="async"
                    className="mx-auto h-40 w-auto object-contain sm:h-48"
                />

                {step === 'name' ? (
                    <form onSubmit={handleSubmitLink} className="mx-auto max-w-md space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="intake-patient-name">Nome do paciente</Label>
                            <Input
                                id="intake-patient-name"
                                // The dialog only auto-focuses on open, and the card
                                // button that got us here just unmounted.
                                autoFocus
                                placeholder="Digite o nome do paciente"
                                value={patientName}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    setPatientName(toTitleCase(event.target.value))
                                }
                            />
                        </div>
                        <Button type="submit" className="w-full" disabled={!canSendLink}>
                            Enviar link
                        </Button>
                    </form>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                        <Card className="flex flex-col border-border shadow-sm">
                            <CardHeader className="space-y-3 p-4 sm:p-6">
                                <div className="min-w-0 space-y-1">
                                    <CardTitle className="text-base">
                                        Enviar link de formulário
                                        {patientLinkAccess.isUnavailableInPlan &&
                                        patientLinkAccess.availableFromPlan ? (
                                            <FeatureUnavailableChip
                                                availablePlan={patientLinkAccess.availableFromPlan}
                                                className="ml-2 align-middle"
                                            />
                                        ) : null}
                                    </CardTitle>
                                    <CardDescription className="text-sm text-muted-foreground">
                                        Envie um link para o paciente responder e se cadastrar
                                        automaticamente.
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardFooter className="mt-auto px-4 pb-4 pt-0 sm:px-6 sm:pb-6">
                                <Button
                                    variant="outline"
                                    onClick={goToNameStep}
                                    className="w-full"
                                    aria-disabled={patientLinkAccess.shouldBlock}
                                >
                                    Enviar link
                                </Button>
                            </CardFooter>
                        </Card>

                        <Card className="flex flex-col border-border shadow-sm">
                            <CardHeader className="space-y-3 p-4 sm:p-6">
                                <div className="min-w-0 space-y-1">
                                    <CardTitle className="text-base">
                                        Inserir manualmente
                                        {activePatientAccess.isUnavailableInPlan &&
                                        activePatientAccess.availableFromPlan ? (
                                            <FeatureUnavailableChip
                                                availablePlan={
                                                    activePatientAccess.availableFromPlan
                                                }
                                                className="ml-2 align-middle"
                                            />
                                        ) : null}
                                    </CardTitle>
                                    <CardDescription className="text-sm text-muted-foreground">
                                        Cadastre manualmente os dados do paciente você mesmo.
                                    </CardDescription>
                                </div>
                            </CardHeader>
                            <CardFooter className="mt-auto px-4 pb-4 pt-0 sm:px-6 sm:pb-6">
                                <Button
                                    onClick={handleManual}
                                    className="w-full"
                                    aria-disabled={activePatientAccess.shouldBlock}
                                >
                                    Continuar
                                </Button>
                            </CardFooter>
                        </Card>
                    </div>
                )}
            </div>
        </AppDialogLayout>
    );
}
